import React, { useEffect, useState } from 'react';
import { Tag, Plus, Copy } from 'lucide-react';
import TopBar from '../components/common/TopBar';
import BottomNav from '../components/common/BottomNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import api from '../services/api';
import { toast } from 'sonner';

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const { data } = await api.get('/coupons');
      setCoupons(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const validate = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post(`/coupons/validate/${code}`, null, {
        params: { order_value: 100 }, // dummy value for validation
      });
      toast.success(`Cupom válido! Desconto: R$ ${data.discount_amount.toFixed(2)}`);
      setCode('');
      loadCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cupom inválido');
    } finally {
      setRedeeming(false);
    }
  };

  const copyCode = (cpnCode) => {
    navigator.clipboard.writeText(cpnCode);
    toast.success('Código copiado!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar title="Cupons" />
        <LoadingSpinner size="lg" />
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="coupons-page">
      <TopBar title="Cupons" showBack />

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Redeem */}
        <div className="bg-white rounded-xl border border-border p-4">
          <h2 className="font-['Outfit'] font-bold text-lg mb-3 flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Resgatar cupom
          </h2>
          <div className="flex gap-2">
            <input
              data-testid="coupon-code-input"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Digite o código"
              className="flex-1 px-4 py-2 border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary uppercase"
            />
            <button
              data-testid="validate-coupon"
              onClick={validate}
              disabled={redeeming}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-full font-semibold disabled:opacity-50"
            >
              Validar
            </button>
          </div>
        </div>

        {/* Available coupons */}
        <div>
          <h2 className="font-['Outfit'] font-bold text-lg mb-3">Disponíveis</h2>
          {coupons.length === 0 ? (
            <div className="bg-white rounded-xl border border-border p-8 text-center">
              <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
              <p className="text-muted-foreground">Nenhum cupom disponível</p>
            </div>
          ) : (
            <div className="space-y-3">
              {coupons.map((c) => (
                <div
                  key={c.id}
                  data-testid={`coupon-${c.id}`}
                  className="bg-white rounded-xl border border-border overflow-hidden flex"
                >
                  <div className="w-2 bg-gradient-to-b from-primary to-accent" />
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-bold text-foreground">{c.description}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {c.discount_type === 'percent'
                            ? `${c.discount_value}% de desconto`
                            : `R$ ${c.discount_value.toFixed(2)} de desconto`}
                        </p>
                        {c.min_order_value > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Min: R$ {c.min_order_value.toFixed(2)}
                          </p>
                        )}
                      </div>
                      <button
                        data-testid={`copy-coupon-${c.id}`}
                        onClick={() => copyCode(c.code)}
                        className="px-3 py-1.5 bg-primary/10 text-primary rounded-full font-mono font-bold text-sm flex items-center gap-1 hover:bg-primary/20 transition-colors"
                      >
                        {c.code}
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Coupons;
